import * as React from "react";
import { Grid2X2 } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { ChainProgressIndicator } from "./ChainProgressIndicator";
import SoundWave from "./SoundWave";
import CompassLogo from "./CompassLogo";
import RequestGallery from "./request-gallery/RequestGallery";

interface ChatInputAreaProps {
  galleryId: string;
  requestType: "discuss" | "research";
  handleOpenRequestGallery: () => void;
  isRecording: boolean;
  isMicroLoading: boolean;
  isSpeaking: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  chatMessage: string;
  isSendingMessage: boolean;
  handleInternalTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleInternalKeyPress: (e: React.KeyboardEvent) => void;
  showCompassButton: boolean;
  handleCompassClick?: () => void;
  compassAssetSrc?: string;
  chainProgress: { current: number; total: number } | null;
}

const ChatInputArea = React.forwardRef<HTMLDivElement, ChatInputAreaProps>((p, ref) => (
  <div ref={ref} className="relative flex-1 min-w-0">
    {p.chainProgress && <ChainProgressIndicator chainProgress={p.chainProgress} />}
    <div className="relative pr-1.5 min-h-10 flex items-center border rounded-md overflow-hidden animate-border-pulse focus-within:animate-none transition-shadow duration-300">
      <RequestGallery id={p.galleryId} type={p.requestType} tooltip="Request Gallery">
        <Button
          className="h-8 w-8 p-0 ml-1 rounded-full bg-primary-300 text-neutral-white hover:bg-primary-400 shrink-0 self-end mb-1"
          onClick={p.handleOpenRequestGallery}
        >
          <Grid2X2 className="size-5" />
        </Button>
      </RequestGallery>
      <div className="relative flex-1 min-w-0">
        {(p.isRecording || p.isMicroLoading) && (
          <div className="absolute inset-0 z-0">
            <SoundWave />
          </div>
        )}
        <Textarea
          ref={p.textareaRef as React.RefObject<HTMLTextAreaElement>}
          className="relative z-10 flex-1 w-full border-none focus:ring-0 focus-visible:ring-0 p-0 outline-none resize-none pl-2 bg-transparent text-sm min-h-5 shadow-none py-1.5 placeholder:text-neutral-600 break-words"
          placeholder={p.isRecording || p.isMicroLoading ? "" : "Type or dictate..."}
          value={p.chatMessage}
          onChange={p.handleInternalTextareaChange}
          onKeyDown={p.handleInternalKeyPress}
          rows={1}
          disabled={p.isSendingMessage}
        />
      </div>
      {p.showCompassButton && p.handleCompassClick && p.compassAssetSrc != null && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="h-auto w-auto p-0 ml-1 py-1.5 rounded-full self-end mb-1"
              variant="ghost"
              onClick={p.handleCompassClick}
            >
              <CompassLogo src={p.compassAssetSrc} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Click here to start work.</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  </div>
));
ChatInputArea.displayName = "ChatInputArea";

export { ChatInputArea };
